/* Progressive enhancement of native Shopify Blog tag links and pagination.
 * Without this script every link is an ordinary full-page Shopify route.
 * With it, only the article list is swapped via the Section Rendering API:
 *  - intent prefetch (hover 65ms, touch/pointer down, focus, next page near viewport),
 *  - a click reuses a pending prefetch but never waits for it (first response wins),
 *  - responses cached 5 minutes (12 entries), stale responses can never win,
 *  - swap cross-fades with the View Transitions API where available,
 *  - images stay native: the server-rendered srcset/sizes/loading/fetchpriority are kept.
 */
(() => {
  'use strict';
  if (window.WatchdivesBlogNavigation) {
    window.WatchdivesBlogNavigation.init();
    return;
  }

  let dispose = () => {};
  const init = () => {
    const nav = document.querySelector('[data-wd-blog-navigation]');
    const main = document.getElementById('BlogArticles');
    if (!nav || !main || nav.dataset.navigationReady || !window.fetch || !window.AbortController) return;
    const topicsId = nav.dataset.topicsSectionId;
    const mainId = main.dataset.blogSectionId;
    if (!topicsId || !mainId) return;
    dispose();
    nav.dataset.navigationReady = 'true';

    const base = new URL(nav.dataset.blogUrl, location.origin).pathname.replace(/\/$/, '');
    const status = nav.querySelector('[data-blog-navigation-status]');
    const links = [...nav.querySelectorAll('a[href]')];
    const cache = new Map();
    const pending = new Map();
    const warmed = new Set();
    const stateKey = 'watchdivesBlogNavigation';
    const ttl = 300000;
    const maxEntries = 12;
    let renderedUrl = new URL(location.href);
    let controller;
    let generation = 0;
    let scrollTimer;
    let hoverTimer;
    let loading = false;
    let observer;

    const route = value => {
      const url = new URL(value, location.href);
      if (url.origin !== location.origin || (url.pathname !== base && !url.pathname.startsWith(base + '/tagged/'))) return null;
      if (url.pathname.startsWith(base + '/tagged/') && url.pathname.slice(base.length + 8).includes('/')) return null;
      // Native Blog routes only; never intercept articles, downloads or unrelated query modes.
      for (const key of url.searchParams.keys()) if (!['page', 'preview_theme_id', 'gclid', 'fbclid'].includes(key) && !key.startsWith('utm_')) return null;
      if (url.searchParams.has('page') && !/^[1-9]\d*$/.test(url.searchParams.get('page'))) return null;
      url.hash = '';
      return url;
    };
    const keyOf = url => url.pathname + '?page=' + (url.searchParams.get('page') || '1');
    const isDisabled = link => link.hasAttribute('disabled') || link.getAttribute('aria-disabled') === 'true';
    const stateAt = (url, y = window.scrollY) => ({
      ...(history.state || {}),
      [stateKey]: {url: keyOf(url), y, x: window.scrollX}
    });
    const saveScroll = () => {
      if (!loading && keyOf(new URL(location.href)) === keyOf(renderedUrl)) {
        history.replaceState(stateAt(renderedUrl), '', location.href);
      }
    };
    const fresh = key => {
      const entry = cache.get(key);
      return entry && Date.now() - entry.time < ttl ? entry.payload : null;
    };
    const remember = (url, payload) => {
      const key = keyOf(url);
      const previous = cache.get(key);
      cache.delete(key);
      cache.set(key, {payload, time: previous?.payload === payload ? previous.time : Date.now()});
      if (cache.size > maxEntries) cache.delete(cache.keys().next().value);
    };
    remember(renderedUrl, {
      [topicsId]: nav.closest('[id^="shopify-section-"]').outerHTML,
      [mainId]: main.closest('[id^="shopify-section-"]').outerHTML
    });
    history.replaceState(stateAt(renderedUrl), '', location.href);
    const previousRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';

    const busy = (value, destination) => {
      loading = value;
      main.removeAttribute('aria-busy');
      nav.setAttribute('aria-busy', String(value));
      for (const link of [...links, ...main.querySelectorAll('pagination-theme a[href]')]) {
        link.classList.toggle('is-pending', Boolean(value && destination && route(link.href) && keyOf(route(link.href)) === keyOf(destination)));
      }
      if (value && status) status.textContent = nav.dataset.loadingLabel;
    };
    const fetchSections = async (url, signal, priority) => {
      const request = new URL(url.href);
      // Preview context belongs to the current theme; never put section parameters in browser history.
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) request.searchParams.set('preview_theme_id', preview);
      request.searchParams.set('sections', [topicsId, mainId].join(','));
      const response = await fetch(request.href, {signal, priority, credentials: 'same-origin', headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('Blog sections unavailable');
      const payload = await response.json();
      if (typeof payload[topicsId] !== 'string' || typeof payload[mainId] !== 'string') throw new Error('Missing Blog section');
      return payload;
    };
    const parse = payload => {
      const parser = new DOMParser();
      const nextNav = parser.parseFromString(payload[topicsId], 'text/html').querySelector('[data-wd-blog-navigation]');
      const nextMain = parser.parseFromString(payload[mainId], 'text/html').getElementById('BlogArticles');
      if (!nextNav || !nextMain || nextMain.dataset.blogSectionId !== mainId || !nextMain.querySelector('.wd-blog-list-heading')) throw new Error('Invalid Blog markup');
      // Section scripts are not replayed.
      nextMain.querySelectorAll('script').forEach(script => script.remove());
      // Other image-ratio modes still use the theme's legacy lazy markup; give them native loading.
      for (const img of nextMain.querySelectorAll('img[data-srcset]')) {
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
        if (!img.getAttribute('sizes')) {
          img.sizes = img.closest('.columns')?.classList.contains('medium-6')
            ? '(min-width: 1380px) 625px, (min-width: 768px) calc((100vw - 130px) / 2), calc(100vw - 30px)'
            : '(min-width: 1380px) 407px, (min-width: 768px) calc((100vw - 160px) / 3), calc(100vw - 30px)';
        }
        img.srcset = img.dataset.srcset;
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
      }
      return {nextNav, nextMain};
    };
    const canPrefetch = () => {
      const connection = navigator.connection;
      return document.visibilityState !== 'hidden' && !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '');
    };
    // Warm only the eager first-row covers of a page the reader is about to open, at the
    // exact srcset/sizes the browser will choose, so the swap paints from the HTTP cache.
    const warmCovers = payload => {
      if (!canPrefetch()) return;
      let nextMain;
      try { ({nextMain} = parse(payload)); } catch { return; }
      for (const source of [...nextMain.querySelectorAll('img.wd-blog-card-image[loading="eager"]')].slice(0, 3)) {
        const key = source.getAttribute('src');
        if (!key || warmed.has(key) || warmed.size >= 36) continue;
        warmed.add(key);
        const img = new Image();
        img.fetchPriority = 'low';
        img.decoding = 'async';
        if (source.getAttribute('sizes')) img.sizes = source.getAttribute('sizes');
        if (source.getAttribute('srcset')) img.srcset = source.getAttribute('srcset');
        img.src = key;
      }
    };
    // One shared request per destination; speculative requests are low priority.
    const request = (url, priority) => {
      const key = keyOf(url);
      const existing = pending.get(key);
      if (existing) return existing;
      const requestController = new AbortController();
      const timer = setTimeout(() => requestController.abort(), 12000);
      const promise = fetchSections(url, requestController.signal, priority).then(payload => {
        remember(url, payload);
        return payload;
      }).finally(() => {
        clearTimeout(timer);
        if (pending.get(key)?.promise === promise) pending.delete(key);
      });
      const entry = {promise, controller: requestController, priority};
      pending.set(key, entry);
      promise.catch(() => {});
      return entry;
    };
    const prefetch = url => {
      if (!url || !canPrefetch() || keyOf(url) === keyOf(renderedUrl)) return;
      const key = keyOf(url);
      const cached = fresh(key);
      if (cached) { warmCovers(cached); return; }
      if (pending.has(key) || pending.size >= 2) return;
      request(url, 'low').promise.then(warmCovers, () => {});
    };
    // A click takes an unfinished prefetch if it wins, but always starts a foreground request too.
    const load = async (url, signal) => {
      const key = keyOf(url);
      const cached = fresh(key);
      if (cached) return cached;
      const speculative = pending.get(key);
      if (!speculative || speculative.priority === 'high') {
        const entry = speculative || request(url, 'high');
        // A superseded click cancels its own foreground request.
        const cancel = () => entry.controller.abort();
        signal.addEventListener('abort', cancel, {once: true});
        try { return await entry.promise; } finally { signal.removeEventListener('abort', cancel); }
      }
      pending.delete(key);
      const foregroundController = new AbortController();
      const abortBoth = () => { foregroundController.abort(); speculative.controller.abort(); };
      signal.addEventListener('abort', abortBoth, {once: true});
      const foreground = fetchSections(url, foregroundController.signal, 'high');
      foreground.catch(() => {});
      try {
        const payload = await Promise.any([speculative.promise, foreground]);
        remember(url, payload);
        return payload;
      } catch {
        throw new Error('Blog sections unavailable');
      } finally {
        signal.removeEventListener('abort', abortBoth);
        foregroundController.abort();
        speculative.controller.abort();
      }
    };
    const watchPagination = () => {
      observer?.disconnect();
      const next = main.querySelector('pagination-theme .next a[href]');
      if (!next || isDisabled(next) || !window.IntersectionObserver) return;
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect();
          prefetch(route(next.href));
        }
      }, {rootMargin: '400px 0px'});
      observer.observe(next);
    };
    const intentLink = event => {
      const link = event.target.closest?.('a[href]');
      if (!link || isDisabled(link)) return null;
      if (!nav.contains(link) && !main.querySelector('pagination-theme')?.contains(link)) return null;
      return link;
    };
    const onHover = event => {
      const link = intentLink(event);
      clearTimeout(hoverTimer);
      if (link) hoverTimer = setTimeout(() => prefetch(route(link.href)), 65);
    };
    const onIntent = event => {
      const link = intentLink(event);
      if (link) prefetch(route(link.href));
    };
    const swap = (commit, animate) => {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (animate && document.startViewTransition && !reduce && document.visibilityState === 'visible') {
        document.startViewTransition(commit);
      } else commit();
    };
    const navigate = async (url, {push = true, pagination = false, restore} = {}) => {
      saveScroll();
      clearTimeout(hoverTimer);
      controller?.abort();
      const ownGeneration = ++generation;
      controller = new AbortController();
      const signal = controller.signal;
      const timeout = setTimeout(() => controller?.signal === signal && controller.abort(), 12000);
      busy(true, url);
      try {
        const payload = await load(url, signal);
        if (ownGeneration !== generation) return;
        if (signal.aborted) throw new Error('Blog request timed out');
        const {nextNav, nextMain} = parse(payload);
        const nextActive = new Set([...nextNav.querySelectorAll('a[aria-current="page"]')].map(link => new URL(link.getAttribute('href'), location.origin).pathname));
        const announcement = nextMain.querySelector('.wd-blog-list-heading').textContent.trim();
        const decodeText = text => new DOMParser().parseFromString(text, 'text/html').documentElement.textContent;
        observer?.disconnect();
        remember(url, payload);
        renderedUrl = url;
        if (push) history.pushState(stateAt(url, window.scrollY), '', url.href);
        busy(false);
        swap(() => {
          // Keep the header, category links, focus and article container mounted.
          main.replaceChildren(...document.importNode(nextMain, true).childNodes);
          for (const link of links) {
            const active = nextActive.has(new URL(link.href).pathname);
            link.classList.toggle('is-active', active);
            if (active) link.setAttribute('aria-current', 'page');
            else link.removeAttribute('aria-current');
          }
          if (restore) window.scrollTo({top: restore.y || 0, left: restore.x || 0, behavior: 'instant'});
          else if (pagination) {
            main.querySelector('h2')?.focus({preventScroll: true});
            // Align the new page under the sticky header (scroll-margin-top) without a long animated scroll.
            main.scrollIntoView({block: 'start', behavior: 'instant'});
          }
        }, !restore);
        document.title = nextNav.dataset.pageTitle ? decodeText(nextNav.dataset.pageTitle) : document.title;
        document.querySelector('link[rel="canonical"]')?.setAttribute('href', nextNav.dataset.canonicalUrl || url.href);
        document.querySelector('meta[name="description"]')?.setAttribute('content', decodeText(nextNav.dataset.pageDescription || ''));
        for (const name of ['pageTitle', 'canonicalUrl', 'pageDescription']) nav.dataset[name] = nextNav.dataset[name] || '';
        if (status) status.textContent = announcement;
        watchPagination();
        document.dispatchEvent(new CustomEvent('wd:blog:updated', {detail: {url: url.href}}));
      } catch (error) {
        if (ownGeneration !== generation) return;
        busy(false);
        // A network/server failure falls back to the same real link, never an empty list.
        window.location.assign(url.href);
      } finally {
        clearTimeout(timeout);
        if (ownGeneration === generation && loading) busy(false);
      }
    };

    const onClick = event => {
      if (event.defaultPrevented) return;
      const link = event.target.closest('a');
      if (!link || (!nav.contains(link) && !main.querySelector('pagination-theme')?.contains(link))) return;
      // Cached/older markup may still contain disabled anchors with empty/self URLs.
      if (isDisabled(link)) {
        event.preventDefault();
        return;
      }
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.hasAttribute('download') || (link.target && link.target !== '_self') || !link.getAttribute('href')) return;
      const url = route(link.href);
      if (!url) return;
      event.preventDefault();
      if (keyOf(url) === keyOf(renderedUrl) && !loading) return;
      navigate(url, {pagination: !nav.contains(link)});
    };
    const onPop = event => {
      clearTimeout(scrollTimer);
      const url = route(location.href);
      if (!url) { window.location.assign(location.href); return; }
      navigate(url, {push: false, restore: event.state?.[stateKey] || {y: 0, x: 0}});
    };
    const onScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(saveScroll, 600);
    };
    document.addEventListener('click', onClick);
    document.addEventListener('pointerover', onHover);
    document.addEventListener('pointerdown', onIntent);
    document.addEventListener('touchstart', onIntent, {passive: true});
    document.addEventListener('focusin', onIntent);
    window.addEventListener('popstate', onPop);
    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('pagehide', saveScroll);
    watchPagination();
    dispose = () => {
      generation++;
      controller?.abort();
      clearTimeout(scrollTimer);
      clearTimeout(hoverTimer);
      observer?.disconnect();
      for (const entry of pending.values()) entry.controller.abort();
      document.removeEventListener('click', onClick);
      document.removeEventListener('pointerover', onHover);
      document.removeEventListener('pointerdown', onIntent);
      document.removeEventListener('touchstart', onIntent);
      document.removeEventListener('focusin', onIntent);
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', saveScroll);
      history.scrollRestoration = previousRestoration;
      delete nav.dataset.navigationReady;
      busy(false);
    };
  };
  window.WatchdivesBlogNavigation = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
  document.addEventListener('shopify:section:load', init);
})();
